import { Component, Input, ViewChild, OnDestroy } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { Layout } from 'modules/layout';
import { DomainService, DomainWithDnsInformation } from 'services/domain.service';
import { NotificationService } from 'services/notification.service';
import { TaskService } from 'services/task.service';
import { DomainDnsDetailsComponent } from './domain-dns-details.component';
import { DnsRecordModel } from 'models/generated/Distribution.Api.Models.DnsRecord';
import { ListView } from 'modules/list-view';
import { DnsRecordType } from 'enums/DnsRecordType';
import { CredentialType } from 'enums/CredentialType';
import { Pipes } from 'modules/pipes';
import { TooltipComponent } from 'components/shared/tooltip/tooltip.component';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { Inputs } from 'modules/inputs';
import { FormsModule } from '@angular/forms';
import { DnsResponseStatus } from 'enums/DnsResponseStatus';
import { CommunicationLogComponent } from 'components/task/taskflow/communication-log.component';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { PushService } from 'services/push.service';
import { Subscription } from 'rxjs';
import { CommunicationService } from 'services/communication.service';
import { NavigationService } from 'services/navigation.service';
import { ChangeDomainOwnerComponent } from './change-domain-owner.component';
import { RequestDomainTransferComponent } from './request-domain-transfer.component';
import { ChangeDomainEmailComponent } from 'components/hosting/company-domains/change-domain-email.component';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { CredentialService } from 'services/credential.service';
import { NewDomainProductPlanComponent } from './new-domain-product-plan.component';

@Component({
	selector: 'domain-details',
	templateUrl: './domain-details.component.html',
	imports: [Layout, ListView, DomainDnsDetailsComponent, CommunicationLogComponent, Pipes, TooltipComponent, Inputs, FormsModule]
})
export class DomainDetailsComponent implements OnDestroy {

	private _domain!: DomainWithDnsInformation;

	dataProvider: CompanyDataProvider;

	@Input() companyId!: string;
	@Input() set domain(value: DomainWithDnsInformation) {
		this._domain = value;
		if (value.productPlanId || value.awaitingTaskActionId)
			this.taskService.getCompanyTaskflows(this.dataProvider.companyId).then(taskflows => {
				if (value.productPlanId) {
					this.domainLogTaskFlowIds = taskflows
						.filter(tf => tf.productPlanId === value.productPlanId && tf.id != null)
						.map(tf => tf.id!);
					this.getCommunicationLog();
				}
				if (value.awaitingTaskActionId) {
					this.awaitingTaskAction = taskflows.flatMap(tf => tf.taskActions).find(action => action.id === value.awaitingTaskActionId);
				}
			})
		this.resetView();
	}
	get domain() { return this._domain; }
	@ViewChild('dnsAdminButton') dnsAdminButton?: ButtonComponent;
	@ViewChild('getAuthIdButton') getAuthIdButton?: ButtonComponent;
	@ViewChild('submitAuthIdButton') submitAuthIdButton?: ButtonComponent;

	dnsRecords: DnsRecordModel[] | null = null;
	dnsResponseStatusEnum = DnsResponseStatus;
	dnsResponseStatus?: DnsResponseStatus;
	recordType = DnsRecordType;
	domainCleanUpCredential = CredentialType.DomainCleanUp;
	editDnsCredential = CredentialType.EditDnsRecords;
	domainTransferCredential = CredentialType.DomainRedelegate;
	lastStatusCheckTimestamp?: Date;
	canGetAuthId = false;
	authId = '';
	isResetable = false;
	domainLogTaskFlowIds?: string[];
	communicationLog: CommunicationLogEntryModel[] = [];
	onCommunicationLogChanges: Subscription;
	isValidDomainDk: boolean = false;
	awaitingTaskAction?: TaskActionModel = undefined;
	attachToNewPlanPermission = false;
	domainHasProcessableStatus = false;

	private domainStatusTimer?: NodeJS.Timeout;
	private dnsRecordsTimer?: NodeJS.Timeout;

	constructor(private readonly domainService: DomainService,
		private readonly navigation: NavigationService,
		private readonly notificationService: NotificationService,
		private readonly pushService: PushService,
		private readonly communicationService: CommunicationService,
		private readonly taskService: TaskService,
		credentialService: CredentialService,
		dataProviderFactory: CompanyDataProviderFactory) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();

		this.onCommunicationLogChanges = this.pushService.onCommunicationLogEntryChanged.subscribe(event => {
			if (event.data.companyId === this.dataProvider.companyId) {
				this.getCommunicationLog();
			}
		});

		this.attachToNewPlanPermission = credentialService.hasCredentials(CredentialType.EditAccountingPlanEndDate, CredentialType.StartProductPlanManually);
	}

	getCommunicationLog() {
		this.communicationService.getLogEntries(this.dataProvider.companyId).then(logs => {
			this.communicationLog = logs.filter(l => l.taskflowId != null && this.domainLogTaskFlowIds?.includes(l.taskflowId));
		});
	}

	resetView() {
		this.lastStatusCheckTimestamp = undefined;
		this.dnsRecords = null;
		this.dnsResponseStatus = undefined;
		this.authId = '';
		this.isValidDomainDk = this.domain.name.isValidDomainDk();
		this.canGetAuthId = this.isValidDomainDk && this.isRegistrarManaged();

		this.refreshDomainStatus();
		this.refreshDnsRecords();
	}

	removeDomain() {
		this.notificationService.confirm('company.domains.remove-confirm', { 'domain': this.domain.name }).subscribe(async confirmed => {
			if (confirmed) {
				await this.domainService.removeDomain(this.domain.id!);
				this.domain.deleted = new Date();
			}
		});
	}

	resetRegistrarAction() {
		this.notificationService.confirm('company.domains.reset-confirm', { 'domain': this.domain.name }).subscribe(async confirmed => {
			if (confirmed) {
				await this.domainService.resetRegistrarAction(this.domain.id!);
			}
		});
	}

	async changeDomainOwner() {
		const view = await this.navigation.openPopup(ChangeDomainOwnerComponent);
		view.domainId = this.domain.id;
		view.currentOwner = await this.dataProvider.getCompany();
	}

	async refreshDomainStatus(forceCheck: boolean = false) {
		if (this.domainStatusTimer) clearTimeout(this.domainStatusTimer);
		this.isResetable = false;
		if (this.domain.status && this.domain.status.filter(s => s == 'awaiting-authid' || s == 'awaiting-transfer').length > 0) {
			this.isResetable = true;
		}
		if (!forceCheck && this.domain.status?.length == 0) return;
		await this.domainService.getDomainStatus(this.domain, true);
		this.domainHasProcessableStatus = this.domain.status ? this.domain.status?.some(s => !this.domainService.isWarningStatus(s)) : false;
		this.lastStatusCheckTimestamp = new Date();
		this.domainStatusTimer = setTimeout(() => { this.refreshDomainStatus(); }, 10000);
	}

	async refreshDnsRecords() {
		if (this.dnsRecordsTimer) clearTimeout(this.dnsRecordsTimer);
		if (this.dnsRecords !== null) return;
		await this.getDnsRecords();
		this.dnsRecordsTimer = setTimeout(() => { this.refreshDnsRecords(); }, 10000);
	}

	async transferToRegistrar() {
		if (!this.domain.id || !this.companyId) return;

		const view = await this.navigation.openPopup(RequestDomainTransferComponent, 'company.domains.request-transfer.header');
		view.domain = this.domain;
	}

	async attachToNewPlan() {
		const view = await this.navigation.openPopup(NewDomainProductPlanComponent, 'company.domains.new-product-plan.header');
		view.domain = this.domain;
	}

	async getDnsRecords() {
		if (!this.domain) return;
		const dnsRecordsResponse = await this.domainService.getDnsRecords(this.domain.name);

		this.dnsResponseStatus = dnsRecordsResponse.responseStatus;
		if (dnsRecordsResponse.responseStatus !== DnsResponseStatus.Ok) {
			this.dnsRecords = null;
			return;
		}

		this.dnsRecords = dnsRecordsResponse.records.filter(record => record.type != DnsRecordType.NS) ?? null;
	}

	async openDnsAdmin() {
		if (!this.domain) return;
		const url = await this.domainService.getDnsAdminUrl(this.domain.name);
		if (url) window.open(url, '_blank');
		this.dnsAdminButton?.reset();
	}

	async addDnsForDomain() {
		if (!this.domain?.id) return;

		let registrantInfo = this.domainService.getRegistrantInfo(await this.dataProvider.getCompany());
		await this.domainService.addDnsToDomain(this.domain.id, registrantInfo);
		this.refreshDnsRecords();
		this.refreshDomainStatus(true);
		// Don't reset button status, because we expect it to disappear anyway once status is refreshed
	}

	isOurNameserver() { return this.domain.nameServer && this.domainService.isOurNameserver(this.domain.nameServer); }
	isRegistrarManaged() { return this.domainService.domainHasPlan(this.domain); }

	async getAuthId() {
		if (!this.canGetAuthId) return;
		this.domain.authId = await this.domainService.getDomainAuthId(this.domain.name);
		this.getAuthIdButton?.reset();
	}

	copyAuthId() {
		if (this.domain.authId)
			navigator.clipboard.writeText(this.domain.authId);
	}

	changeDomainEmail() {
		this.navigation.openPopup(ChangeDomainEmailComponent).then(view => {
			view.domain = this.domain;
		});
	}

	async submitAuthId() {
		if (!this.domain?.id || !this.authId) return;
		try {
			await this.domainService.submitAuthId(this.domain.id, this.authId);
		}
		catch (error) {
			this.submitAuthIdButton?.reset();
		}
	}

	ngOnDestroy() {
		if (this.domainStatusTimer) clearTimeout(this.domainStatusTimer);
		if (this.dnsRecordsTimer) clearTimeout(this.dnsRecordsTimer);
		this.onCommunicationLogChanges.unsubscribe();
	}

	isWarningStatus(status: string) {
		return this.domainService.isWarningStatus(status);
	}
}
